import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';

const TrackNetworkingROI = () => {
  useSEO({
    title: "How to Track Your Networking ROI | AM CARDMATE Blog",
    description: "Learn how to measure and optimize your networking effectiveness with analytics and data-driven insights.",
    image: "/assets/networkroi.jpg",
    keywords: "networking ROI, analytics, track networking, measure success, business metrics"
  });

  useEffect(() => {
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": "How to Track Your Networking ROI",
        "description": "Learn how to measure and optimize networking effectiveness with analytics",
        "datePublished": "2025-05-22",
        "dateModified": "2025-05-22",
        "author": { "@type": "Organization", "name": "AM CARDMATE Analytics Team" },
        "publisher": { "@type": "Organization", "name": "AM CARDMATE", "logo": { "@type": "ImageObject", "url": "https://amcardmate.com/assets/logo.svg" } },
        "image": "https://amcardmate.com/assets/networkroi.jpg",
        "mainEntityOfPage": { "@type": "WebPage", "@id": "https://amcardmate.com/blog/track-networking-roi" }
      }
    ];
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, []);

  const [selectedMetric, setSelectedMetric] = useState('views');
  const [roiCalculator, setRoiCalculator] = useState({ cardsGiven: 100, avgDealSize: 2500, conversionRate: 5 });

  const calculateROI = () => {
    const leads = roiCalculator.cardsGiven * (roiCalculator.conversionRate / 100);
    const revenue = leads * roiCalculator.avgDealSize;
    const cardCost = 49;
    const roi = ((revenue - cardCost) / cardCost * 100).toFixed(0);
    return { leads, revenue, roi };
  };
  const { leads, revenue, roi } = calculateROI();

  const metrics = {
    views: { title: 'Card Views', text: 'Track total views in any timeframe and see which devices your card is viewed on. Peak viewing times help you optimize follow-up timing.', tip: 'Follow up within 24 hours of a view for 5x higher response rates!', color: 'bg-blue-50' },
    scans: { title: 'QR Code Scans', text: 'Every scan is a direct engagement. Track scan locations, times, and conversion rates to optimize your networking approach.', tip: 'QR scans have 3x higher conversion rates than other contact methods!', color: 'bg-green-50' },
    saves: { title: 'Contact Saves', text: "The golden metric! When someone saves your contact, they're seriously interested. These are your hottest leads.", tip: 'Create a special follow-up sequence for contact savers. They\'re 10x more likely to convert!', color: 'bg-purple-50' },
    shares: { title: 'Contact Shares', text: "When people share your contact with others, you're getting referrals! Track viral coefficient and network growth.", tip: 'Add "Feel free to share!" to encourage viral spread!', color: 'bg-pink-50' }
  };

  return (
    <>
      {/* Hero Section */}
      <section className="pt-32 md:pt-36 lg:pt-40 pb-20 bg-gradient-to-b from-white via-cyan-50/10 to-white relative overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto">
            <nav className="flex flex-wrap items-center gap-2 text-caption mb-6">
              <Link to="/" className="hover:text-primary-blue transition-colors">Home</Link>
              <FontAwesomeIcon icon="chevron-right" className="text-xs" />
              <Link to="/blog" className="hover:text-primary-blue transition-colors">Blog</Link>
              <FontAwesomeIcon icon="chevron-right" className="text-xs" />
              <span className="text-primary-blue">Track Networking ROI</span>
            </nav>

            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-100 to-blue-100 rounded-full mb-6">
                <FontAwesomeIcon icon="chart-line" className="text-teal-600" />
                <span className="font-medium text-gray-700 text-body-sm">Analytics Guide</span>
              </div>

              <h1 className="text-h1 mb-6">
                How to Track Your Networking
                <span className="block text-gradient-blue-pink mt-2">ROI with Digital Analytics</span>
              </h1>

              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-caption">
                <span className="flex items-center gap-2"><FontAwesomeIcon icon="user" className="text-primary-blue" /> Analytics Team</span>
                <span className="flex items-center gap-2"><FontAwesomeIcon icon="calendar" className="text-primary-blue" /> May 22, 2025</span>
                <span className="flex items-center gap-2"><FontAwesomeIcon icon="clock" className="text-primary-blue" /> 6 min read</span>
              </div>
            </div>

            <div className="relative rounded-3xl overflow-hidden shadow-2xl mb-12">
              <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=600&fit=crop" alt="Analytics dashboard" className="w-full h-auto" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              <div className="absolute bottom-4 md:bottom-8 left-4 md:left-8 right-4 md:right-8 grid grid-cols-3 gap-3">
                {[{ value: '847', label: 'Total Views', color: 'text-primary-blue' }, { value: '92', label: 'Connections', color: 'text-teal-600' }, { value: '12.3%', label: 'Conversion', color: 'text-green-600' }].map((stat, i) => (
                  <div key={i} className="bg-white/90 backdrop-blur-sm rounded-xl p-3 md:p-4 text-center">
                    <div className={`text-h4 font-bold ${stat.color}`}>{stat.value}</div>
                    <div className="text-caption text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <p className="text-body-lg text-gray-600 mb-8">
              Gone are the days of blindly handing out business cards and hoping for the best. With AM CARDMATE's analytics, you can track every interaction, measure your networking effectiveness, and optimize your approach like a data-driven pro. Let's turn your networking from guesswork into a science!
            </p>

            <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl p-6 md:p-8 mb-12">
              <h3 className="text-h4 text-dark mb-4 flex items-center gap-3">
                <FontAwesomeIcon icon="lightbulb" className="text-teal-600" />
                The Power of Data
              </h3>
              <p className="text-body text-gray-600">Companies using data-driven networking strategies see <strong>3x higher conversion rates</strong> and <strong>67% better lead quality</strong>. It's time to join them!</p>
            </div>

            {/* Analytics Dashboard */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-blue to-teal-500 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="tachometer-alt" />
                </div>
                <h2 className="text-h3 text-dark">Key Metrics That Matter</h2>
              </div>

              <div className="bg-gray-50 rounded-3xl p-6 md:p-8">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
                  {[
                    { id: 'views', name: 'Card Views', icon: 'eye' },
                    { id: 'scans', name: 'QR Scans', icon: 'qrcode' },
                    { id: 'saves', name: 'Saves', icon: 'save' },
                    { id: 'shares', name: 'Shares', icon: 'share-alt' }
                  ].map((m) => (
                    <button key={m.id} onClick={() => setSelectedMetric(m.id)} className={`px-4 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 text-body-sm ${selectedMetric === m.id ? 'bg-gradient-to-r from-primary-blue to-teal-500 text-white shadow-lg' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
                      <FontAwesomeIcon icon={m.icon} />
                      <span className="hidden sm:inline">{m.name}</span>
                    </button>
                  ))}
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <h3 className="text-h4 text-dark mb-4">{metrics[selectedMetric].title}</h3>
                  <p className="text-body text-gray-600 mb-4">{metrics[selectedMetric].text}</p>
                  <div className={`${metrics[selectedMetric].color} rounded-xl p-4`}>
                    <p className="text-body-sm text-gray-700"><strong>Pro tip:</strong> {metrics[selectedMetric].tip}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ROI Calculator */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-teal-600 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="calculator" />
                </div>
                <h2 className="text-h3 text-dark">Calculate Your Networking ROI</h2>
              </div>

              <div className="bg-gradient-to-r from-primary-blue to-accent-purple text-white rounded-3xl p-6 md:p-8">
                <p className="text-body text-white/90 mb-6">Adjust the values below to see your potential ROI:</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
                  {[
                    { label: 'Cards Given Out', key: 'cardsGiven' },
                    { label: 'Avg Deal Size (£)', key: 'avgDealSize' },
                    { label: 'Conversion Rate (%)', key: 'conversionRate' }
                  ].map((field) => (
                    <div key={field.key}>
                      <label className="block text-white/80 mb-2 text-body-sm">{field.label}</label>
                      <input type="number" value={roiCalculator[field.key]} onChange={(e) => setRoiCalculator({...roiCalculator, [field.key]: parseInt(e.target.value) || 0})} className="w-full px-4 py-2 rounded-lg text-dark text-body" />
                    </div>
                  ))}
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
                    <div><div className="text-h3 font-bold text-white">{leads.toFixed(0)}</div><div className="text-white/80 text-body-sm">Qualified Leads</div></div>
                    <div><div className="text-h3 font-bold text-white">£{revenue.toLocaleString()}</div><div className="text-white/80 text-body-sm">Potential Revenue</div></div>
                    <div><div className="text-h3 font-bold text-yellow-300">{roi}%</div><div className="text-white/80 text-body-sm">ROI</div></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced Strategies */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="brain" />
                </div>
                <h2 className="text-h3 text-dark">Advanced Analytics Strategies</h2>
              </div>

              <div className="space-y-4">
                {[
                  { bg: 'from-blue-50 to-teal-50', title: '1. The Hot Lead Alert System', text: "Set up notifications for high-value actions. When someone views your profile 3+ times or clicks multiple links, that's a buying signal!", tip: 'Create a follow-up sequence: 1st view = connection request, 2nd = personalized message, 3rd = call invitation.' },
                  { bg: 'from-purple-50 to-pink-50', title: '2. A/B Testing Your Profile', text: 'Create two versions of your profile with different CTAs, headlines, or layouts. Track which converts better.', tip: '"Schedule a Call" vs "Download My Guide", Professional photo vs Casual photo, Short bio vs Detailed bio.' },
                  { bg: 'from-green-50 to-teal-50', title: '3. Event ROI Tracking', text: 'Create unique cards for each event. Compare performance to identify your most valuable networking venues.', tip: 'Cost per lead by event, conversion rate by event type, follow-up response rates.' },
                  { bg: 'from-pink-50 to-yellow-50', title: '4. Time-Based Analysis', text: 'Track when your cards get the most engagement. Schedule follow-ups during peak activity times.', tip: 'Tuesday-Thursday 10am-2pm for B2B, Evenings for B2C, Sundays for entrepreneurs.' }
                ].map((strategy, i) => (
                  <div key={i} className={`bg-gradient-to-r ${strategy.bg} rounded-2xl p-6 hover:shadow-md transition-all`}>
                    <h3 className="text-h5 text-dark mb-3">{strategy.title}</h3>
                    <p className="text-body text-gray-600 mb-4">{strategy.text}</p>
                    <div className="bg-white rounded-xl p-4">
                      <p className="text-body-sm text-gray-700"><strong>Implementation:</strong> {strategy.tip}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Weekly Routine */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-blue to-blue-700 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="calendar-check" />
                </div>
                <h2 className="text-h3 text-dark">Your Weekly Analytics Routine</h2>
              </div>

              <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 space-y-6">
                {[
                  { day: 'M', gradient: 'from-primary-blue to-teal-500', title: 'Monday: Review Weekend Activity', text: 'Check who viewed your profile over the weekend. Send personalised follow-ups whilst you\'re fresh in their mind.' },
                  { day: 'W', gradient: 'from-purple-400 to-pink-500', title: 'Wednesday: Mid-Week Metrics Check', text: 'Analyse link clicks and contact saves. Adjust your profile based on what\'s working.' },
                  { day: 'F', gradient: 'from-green-400 to-teal-500', title: 'Friday: Weekly Report & Planning', text: "Review full week's performance. Plan next week's networking strategy based on data insights." }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${item.gradient} rounded-xl flex items-center justify-center text-white flex-shrink-0`}>
                      <span className="font-bold text-body-lg">{item.day}</span>
                    </div>
                    <div>
                      <h4 className="text-card-title text-dark mb-2">{item.title}</h4>
                      <p className="text-body text-gray-600">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Benchmarks */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-pink-500 rounded-xl flex items-center justify-center text-white">
                  <FontAwesomeIcon icon="trophy" />
                </div>
                <h2 className="text-h3 text-dark">Benchmarks for Success</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6">
                  <h3 className="text-card-title text-dark mb-4">Good Performance</h3>
                  <ul className="space-y-2 text-body text-gray-600">
                    {['10-15% view-to-save rate', '20-30% link click rate', '5-10% share rate'].map((item, i) => (
                      <li key={i} className="flex items-center gap-2"><FontAwesomeIcon icon="check" className="text-green-500" /><span>{item}</span></li>
                    ))}
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-6">
                  <h3 className="text-card-title text-dark mb-4">Excellent Performance</h3>
                  <ul className="space-y-2 text-body text-gray-600">
                    {['15%+ view-to-save rate', '30%+ link click rate', '10%+ share rate'].map((item, i) => (
                      <li key={i} className="flex items-center gap-2"><FontAwesomeIcon icon="star" className="text-yellow-500" /><span>{item}</span></li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-[#4DA2FF] to-[#DE6161] relative overflow-hidden">
        <div className="absolute inset-0 bg-dots-pattern opacity-10"></div>
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center">
            <h2 className="text-h2 text-white mb-6">Your 30-Day Analytics Action Plan</h2>
            <div className="max-w-2xl mx-auto space-y-4 mb-8 text-left">
              {['Set up your analytics dashboard and familiarize yourself with all metrics', 'Establish baseline metrics - where are you starting from?', 'Implement weekly review routine and stick to it', 'Test one optimisation per week based on data insights'].map((step, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-white text-body-lg">{i + 1}</span>
                  </div>
                  <p className="text-body text-white/90">{step}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/personal-card" className="btn bg-white text-primary-blue hover:bg-gray-100 transition-all shadow-lg">
                <FontAwesomeIcon icon="chart-line" />
                <span>Start Tracking Today</span>
              </Link>
              <Link to="/business-card" className="btn bg-white/20 text-white hover:bg-white/30 transition-all">
                <FontAwesomeIcon icon="briefcase" />
                <span>Business Analytics</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Author & Related */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="border-t pt-8 mb-12">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white font-bold text-body-lg flex-shrink-0">AT</div>
                <div>
                  <h3 className="text-card-title text-dark">Analytics Team</h3>
                  <p className="text-body-sm text-gray-500 mb-2">AM CARDMATE Data Science Department</p>
                  <p className="text-body-sm text-gray-600">We're the data nerds who turn numbers into networking gold. Our mission? Helping you make smarter connections through analytics. Fun fact: We've analyzed over 10 million card interactions!</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-h3 text-dark mb-6">Level Up Your Game</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {[
                  { to: '/blog/creative-ways-use-nfc-card', title: '10 Creative Ways to Use Your NFC Card', gradient: 'from-pink-400 to-yellow-400' },
                  { to: '/blog/setting-up-digital-business-card', title: 'Setting Up Your Digital Business Card', gradient: 'from-blue-400 to-purple-500' },
                  { to: '/blog/future-of-networking', title: 'The Future of Networking', gradient: 'from-purple-500 to-pink-500' }
                ].map((post, i) => (
                  <Link key={i} to={post.to} className={`group ${i === 2 ? 'sm:col-span-2 md:col-span-1' : ''}`}>
                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300">
                      <div className={`h-40 bg-gradient-to-br ${post.gradient}`}></div>
                      <div className="p-4">
                        <h4 className="text-body font-bold text-dark group-hover:text-primary-blue transition-colors">{post.title}</h4>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default TrackNetworkingROI;